import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  FileText, Clock, Loader2, CheckCircle2, XCircle, Send, Building2,
  Eye, Pencil, Trash2, AlertTriangle, Download, Search, Filter, FileSignature
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  brouillon: { label: 'Brouillon', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  envoye: { label: 'Envoye', cls: 'bg-blue-100 text-blue-700 border-blue-300' },
  recu: { label: 'Recu', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  signe: { label: 'Signe', cls: 'bg-green-100 text-green-700 border-green-300' },
  refuse: { label: 'Refuse', cls: 'bg-red-100 text-red-700 border-red-300' },
};

const safeFmt = (d, fmt = 'dd/MM/yyyy') => {
  try { return format(parseISO(d), fmt, { locale: fr }); } catch { return '-'; }
};

function DetailRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <tr className="border-b border-slate-100 last:border-0">
      <td className="py-2 pr-4 text-xs text-slate-500 w-40">{label}</td>
      <td className="py-2 text-sm font-medium text-slate-800">{value}</td>
    </tr>
  );
}

export default function ContractRequestsPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [viewDetail, setViewDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    try {
      const [allRes, statsRes] = await Promise.all([
        fetch(`${API}/api/contract-requests/all`, { headers }),
        fetch(`${API}/api/contract-requests/dashboard-stats`, { headers }),
      ]);
      if (allRes.ok) setRequests(await allRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (reqId, newStatus) => {
    setUpdatingId(reqId);
    try {
      const res = await fetch(`${API}/api/contract-requests/${reqId}/status`, {
        method: 'PATCH', headers, body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) { toast.success('Statut mis a jour'); load(); }
      else toast.error('Erreur');
    } catch { toast.error('Erreur reseau'); }
    finally { setUpdatingId(null); }
  };

  const handleView = async (reqId) => {
    setLoadingDetail(true);
    try {
      const res = await fetch(`${API}/api/contract-requests/${reqId}`, { headers });
      if (res.ok) setViewDetail(await res.json());
      else toast.error('Erreur chargement');
    } catch { toast.error('Erreur reseau'); }
    finally { setLoadingDetail(false); }
  };

  const handleDelete = async (reqId) => {
    setDeleting(true);
    try {
      const res = await fetch(`${API}/api/contract-requests/${reqId}`, { method: 'DELETE', headers });
      if (res.ok) { toast.success('Demande supprimee'); setDeleteConfirm(null); load(); }
      else { const e = await res.json(); toast.error(e.detail || 'Erreur'); }
    } catch { toast.error('Erreur reseau'); }
    finally { setDeleting(false); }
  };

  const handleExport = async (reqId) => {
    try {
      const res = await fetch(`${API}/api/contract-requests/${reqId}/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const blob = await res.blob();
        const disp = res.headers.get('content-disposition') || '';
        const match = disp.match(/filename=(.+)/);
        const fname = match ? match[1] : 'Demande_Contrat.xlsx';
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = fname; a.click();
        window.URL.revokeObjectURL(url);
      } else toast.error("Erreur export");
    } catch { toast.error('Erreur reseau'); }
  };

  const handleExportAll = async () => {
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('q', search.trim());
      if (filterStatus) params.set('status', filterStatus);
      const qs = params.toString();
      const res = await fetch(`${API}/api/contract-requests/export-excel${qs ? `?${qs}` : ''}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const blob = await res.blob();
        const disp = res.headers.get('content-disposition') || '';
        const match = disp.match(/filename="?([^"]+)"?/);
        const fname = match ? match[1] : 'demandes_contrat.xlsx';
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = fname; a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Export Excel téléchargé');
      } else if (res.status === 403) toast.error('Réservé aux administrateurs');
      else toast.error("Erreur lors de l'export");
    } catch { toast.error('Erreur réseau'); }
  };

  const filtered = requests.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !q || [r.enseigne, r.ville, r.contact_name, r.created_by_name, r.project_name].some(v => (v || '').toLowerCase().includes(q));
    const matchStatus = !filterStatus || r.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const total = stats?.total || 0;
  const d = viewDetail?.data || {};

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="contract-requests-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="page-title">
              <FileSignature className="w-6 h-6 text-emerald-600" />
              Demandes de Contrat
            </h1>
            <p className="text-sm text-slate-500 mt-1">Suivi et gestion de toutes les demandes de contrat</p>
          </div>
          {(user?.role === 'admin' || user?.role === 'super_admin') && (
            <Button onClick={handleExportAll} className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white self-start sm:self-auto" data-testid="export-all-excel-btn">
              <Download className="w-4 h-4" /> Exporter Excel
            </Button>
          )}
        </div>

        {/* KPI Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="contract-kpis">
            <Card className="border-emerald-200 bg-emerald-50/50">
              <CardContent className="p-4 text-center">
                <FileText className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-emerald-900" data-testid="kpi-total">{total}</p>
                <p className="text-xs text-slate-500">Total</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Clock className="w-5 h-5 text-slate-500 mx-auto mb-1" />
                <p className="text-2xl font-bold" data-testid="kpi-brouillon">{stats.brouillon || 0}</p>
                <p className="text-xs text-slate-500">Brouillons</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Send className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                <p className="text-2xl font-bold" data-testid="kpi-envoye">{stats.envoye || 0}</p>
                <p className="text-xs text-slate-500">Envoyes</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50/50">
              <CardContent className="p-4 text-center">
                <CheckCircle2 className="w-5 h-5 text-green-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-green-700" data-testid="kpi-signe">{stats.signe || 0}</p>
                <p className="text-xs text-green-600">Signes</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <XCircle className="w-5 h-5 text-red-500 mx-auto mb-1" />
                <p className="text-2xl font-bold" data-testid="kpi-refuse">{stats.refuse || 0}</p>
                <p className="text-xs text-slate-500">Refuses</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Pipeline Bar */}
        {stats && total > 0 && (
          <div className="flex items-center gap-1 h-6 rounded-lg overflow-hidden" data-testid="pipeline-bar">
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
              const count = stats[key] || 0;
              if (count === 0) return null;
              return (
                <div key={key}
                  className={`${cfg.cls} h-full flex items-center justify-center text-xs font-medium transition-all border`}
                  style={{ width: `${(count / total) * 100}%`, minWidth: '28px' }}
                  title={`${cfg.label}: ${count}`}
                >{count}</div>
              );
            })}
          </div>
        )}

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Rechercher par enseigne, ville, contact..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-10" data-testid="search-input" />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm bg-white" data-testid="status-filter">
            <option value="">Tous les statuts</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p>{search || filterStatus ? 'Aucun resultat pour ce filtre' : 'Aucune demande de contrat'}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Enseigne / Ville</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Createur</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map(req => (
                      <TableRow key={req.id} data-testid={`contract-row-${req.id}`}>
                        <TableCell>
                          <div className="font-medium text-slate-800">{req.enseigne || '-'}</div>
                          <div className="text-xs text-slate-500">{req.ville || '-'}</div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{req.contact_name && req.contact_name !== 'None None' ? req.contact_name : '-'}</TableCell>
                        <TableCell>
                          <select value={req.status || 'brouillon'}
                            onChange={e => updateStatus(req.id, e.target.value)}
                            disabled={updatingId === req.id}
                            className="h-7 text-xs border rounded-md px-2 py-1 bg-white text-slate-700 focus:ring-1 focus:ring-emerald-500"
                            data-testid={`status-select-${req.id}`}>
                            <option value="brouillon">Brouillon</option>
                            <option value="envoye">Envoye</option>
                            <option value="recu">Recu</option>
                            <option value="signe">Signe</option>
                            <option value="refuse">Refuse</option>
                          </select>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">{safeFmt(req.created_at)}</TableCell>
                        <TableCell className="text-sm text-slate-600">{req.created_by_name || '-'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleView(req.id)}
                              title="Voir" data-testid={`view-btn-${req.id}`}>
                              <Eye className="w-3.5 h-3.5 text-blue-600" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                              onClick={() => navigate(`/simulation/${req.simulation_id}?tab=contract`)}
                              title="Modifier" data-testid={`edit-btn-${req.id}`}>
                              <Pencil className="w-3.5 h-3.5 text-amber-600" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                              onClick={() => handleExport(req.id)}
                              title="Exporter" data-testid={`export-btn-${req.id}`}>
                              <Download className="w-3.5 h-3.5 text-emerald-600" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                              onClick={() => setDeleteConfirm(req)}
                              title="Supprimer" data-testid={`delete-btn-${req.id}`}>
                              <Trash2 className="w-3.5 h-3.5 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* View Detail Modal */}
      <Dialog open={!!viewDetail} onOpenChange={() => setViewDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="contract-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <FileText className="w-5 h-5" />
              Demande de Contrat — {d.enseigne} {d.ville}
            </DialogTitle>
          </DialogHeader>
          {viewDetail && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={STATUS_CONFIG[viewDetail.status]?.cls || ''}>
                  {STATUS_CONFIG[viewDetail.status]?.label || viewDetail.status}
                </Badge>
                <span className="text-xs text-slate-500">
                  Cree le {safeFmt(viewDetail.created_at, 'dd/MM/yyyy HH:mm')} par {viewDetail.created_by_name}
                </span>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-2">Informations magasin</h4>
                <table className="w-full"><tbody>
                  <DetailRow label="Enseigne" value={d.enseigne} />
                  <DetailRow label="Raison sociale" value={d.raison_sociale} />
                  <DetailRow label="Capital" value={d.capital ? `${d.capital} EUR` : null} />
                  <DetailRow label="SIRET" value={d.siret} />
                  <DetailRow label="Adresse" value={d.adresse} />
                  <DetailRow label="Code postal" value={d.code_postal} />
                  <DetailRow label="Ville" value={d.ville} />
                  <DetailRow label="Surface (m2)" value={d.surface_m2} />
                </tbody></table>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-2">Dirigeant</h4>
                <table className="w-full"><tbody>
                  <DetailRow label="Genre" value={d.genre_dirigeant} />
                  <DetailRow label="Prenom" value={d.prenom_dirigeant} />
                  <DetailRow label="Nom" value={d.nom_dirigeant} />
                </tbody></table>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-2">Conditions contrat</h4>
                <table className="w-full"><tbody>
                  <DetailRow label="Date mise en place" value={d.date_mise_en_place} />
                  <DetailRow label="Duree" value={d.duree_mois ? `${d.duree_mois} mois` : null} />
                  <DetailRow label="CA minimum mensuel" value={d.ca_minimum_mensuel ? `${d.ca_minimum_mensuel} EUR HT` : null} />
                  <DetailRow label="CA sortie anticipee" value={d.ca_sortie_anticipee ? `${d.ca_sortie_anticipee} EUR HT` : null} />
                  <DetailRow label="Date signature" value={d.date_signature} />
                  <DetailRow label="Taux commission" value={d.taux_commission ? `${d.taux_commission}%` : null} />
                </tbody></table>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-2">Investissement CAPEX</h4>
                <table className="w-full"><tbody>
                  <DetailRow label="Corner" value={d.corner_investor} />
                  <DetailRow label="Vitrine" value={d.vitrine_investor} />
                  <DetailRow label="Inox" value={d.inox_investor} />
                </tbody></table>
              </div>
              {viewDetail.sent_to && (
                <div className="text-xs text-slate-500 pt-2 border-t">
                  Envoye a : <span className="font-medium">{viewDetail.sent_to}</span>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 pt-4 border-t">
            <Button variant="outline" size="sm" onClick={() => setViewDetail(null)} data-testid="close-detail-btn">Fermer</Button>
            {viewDetail && (
              <>
                <Button variant="outline" size="sm" className="gap-1" onClick={() => handleExport(viewDetail.id)} data-testid="export-detail-btn">
                  <Download className="w-3.5 h-3.5" /> Exporter Excel
                </Button>
                <Button size="sm" className="gap-1 bg-amber-500 hover:bg-amber-600 text-white"
                  onClick={() => { setViewDetail(null); navigate(`/simulation/${viewDetail.simulation_id}?tab=contract`); }}
                  data-testid="edit-from-detail-btn">
                  <Pencil className="w-3.5 h-3.5" /> Modifier
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-confirm-modal">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> Supprimer la demande
            </DialogTitle>
          </DialogHeader>
          {deleteConfirm && (
            <p className="text-sm text-slate-600">
              Voulez-vous vraiment supprimer la demande pour <strong>{deleteConfirm.enseigne} - {deleteConfirm.ville}</strong> ?
            </p>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(null)} data-testid="cancel-delete-btn">Annuler</Button>
            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white gap-1"
              onClick={() => handleDelete(deleteConfirm.id)} disabled={deleting} data-testid="confirm-delete-btn">
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {loadingDetail && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 flex items-center gap-3 shadow-lg">
            <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />
            <span className="text-sm text-slate-600">Chargement...</span>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
